<script setup>
import { computed, onMounted, onUnmounted, watch } from 'vue'
import { useStore } from 'vuex'
import { MapLayer, MapSource } from '@/components/map'
import {
  LayerPlanetVars,
  LayerPlanetscope,
  LayerOrder,
  LayerRadar,
  LayerCollectedData,
  LayerStatic,
  LayerTasking
} from '@/components/layers'
import { useLayerManager } from '@/composables/useLayerManager'
import { createLayerId, createSourceId } from '@/components/layers/types'

const emit = defineEmits(['isLoaded', 'deleteLayer'])
const props = defineProps({
  map: Object,
  item: Object,
  position: Number,
  layerId: String,
  orgId: Number,
  isVisible: Boolean,
})

const store = useStore()
const map = computed(() => store.getters['geo/getMap'])
const layerManager = useLayerManager(map)

// Calculate beforeId from position using z-layers
// Position 0 = top of list = top of map = z-0
const beforeId = computed(() => `z-${props.position}`)

// Layer IDs using unified convention
const geometrySourceId = computed(() => createSourceId(props.layerId, 'geometry-source'))
const geometryLayerId = computed(() => createLayerId(props.layerId, 'geometry'))

// Get the geometry from the layer data
const layerGeometry = computed(() => {
  return props.item?.properties?.layer?.geometry ||
         props.item?.geometry ||
         null
})

// Register/unregister group with layer manager
onMounted(() => {
  layerManager.registerGroup(props.layerId, props.position)
  if (layerGeometry.value) {
    layerManager.registerLayer(props.layerId, geometryLayerId.value)
  }
})

onUnmounted(() => {
  if (layerGeometry.value) {
    layerManager.unregisterLayer(props.layerId, geometryLayerId.value)
  }
  layerManager.unregisterGroup(props.layerId)
})

// Watch for position changes (drag-drop reordering)
// When position changes, move all layers in this group to the new z-layer
watch(() => props.position, (newPosition, oldPosition) => {
  if (newPosition !== oldPosition && newPosition !== undefined) {
    layerManager.moveGroupToPosition(props.layerId, newPosition)
  }
})
</script>

<template>
<div class="ml-2">
  <!-- Geometry outline for the layer boundary -->
  <MapSource
    v-if="layerGeometry && isVisible"
    :map="map"
    type="geojson"
    :id="geometrySourceId"
    :data="{
      type: 'Feature',
      geometry: layerGeometry,
      properties: {}
    }"
  >
    <MapLayer
      :map="map"
      :id="geometryLayerId"
      :source="geometrySourceId"
      type="line"
      :before-id="beforeId"
      :group-id="layerId"
      :options="{
        paint: {
          'line-color': '#1E90FF',
          'line-width': 2,
          'line-opacity': 0.8
        },
        layout: {
          visibility: 'visible'
        }
      }"
    />
  </MapSource>

  <!-- Layer variants - each handles its own MapSource/MapLayer -->
  <LayerPlanetscope
    v-if="item.properties.type === 0"
    :map="map"
    :before-id="beforeId"
    :group-id="layerId"
    :org-id="orgId"
    :layer-id="layerId"
    :is-visible="isVisible"
    :options="{
      layout: {
        visibility: isVisible ? 'visible' : 'none',
      },
    }"
    @isLoaded="emit('isLoaded', $event)"
  />

  <LayerOrder
    v-if="item.properties.type === 1"
    :map="map"
    :layer="item.properties"
    :layer-id="layerId"
    :before-id="beforeId"
    :group-id="layerId"
    :is-visible="isVisible"
    :options="{
      minzoom: 0,
      maxzoom: 22,
      layout: {
        visibility: isVisible ? 'visible' : 'none',
      },
    }"
    @isLoaded="emit('isLoaded', $event)"
  />

  <LayerPlanetVars
    v-if="item.properties.type === 2"
    :map="map"
    :layer-id="layerId"
    :code="item.properties.code"
    :layer="item.properties.layer"
    :before-id="beforeId"
    :group-id="layerId"
    :is-visible="isVisible"
    :options="{
      layout: {
        visibility: isVisible ? 'visible' : 'none',
      },
    }"
    @isLoaded="emit('isLoaded', $event)"
    :org-id="orgId"
  />

  <LayerRadar
    v-if="item.properties.type === 3"
    :map="map"
    :layer-id="layerId"
    :code="item.uuid"
    :layer="item.properties.layer"
    :before-id="beforeId"
    :group-id="layerId"
    :is-visible="isVisible"
    :options="{
      layout: {
        visibility: isVisible ? 'visible' : 'none',
      },
    }"
    @isLoaded="emit('isLoaded', $event)"
    :org-id="orgId"
  />

  <LayerCollectedData
    v-if="item.properties.type === 4"
    :map="map"
    :layer-id="layerId"
    :code="item.uuid"
    :layer="item.properties.layer"
    :org-id="orgId"
    :before-id="beforeId"
    :group-id="layerId"
    :is-visible="isVisible"
    :options="{
      layout: {
        visibility: isVisible ? 'visible' : 'none',
      },
    }"
    @isLoaded="emit('isLoaded', $event)"
  />

  <LayerStatic
    v-if="item.properties.type === 5"
    :map="map"
    :layer-id="layerId"
    :code="item.uuid"
    :layer="item.properties.layer"
    :org-id="orgId"
    :before-id="beforeId"
    :group-id="layerId"
    :is-visible="isVisible"
    :options="{
      layout: {
        visibility: isVisible ? 'visible' : 'none',
      },
    }"
    @isLoaded="emit('isLoaded', $event)"
  />

  <LayerTasking
    v-if="item.properties.type === 6"
    :map="map"
    :layer="item"
    :layer-id="layerId"
    :before-id="beforeId"
    :group-id="layerId"
    :is-visible="isVisible"
    :options="{
      layout: {
        visibility: isVisible ? 'visible' : 'none',
      },
    }"
    @isLoaded="emit('isLoaded', $event)"
  />
</div>
</template>
